import { useCallback } from "react";
import { useNavigate, type NavigateFunction } from "react-router";
import { branchChat } from "@/lib/api";
import type { ChatMetadata, DocumentContentBlock, ImageContentBlock } from "@/lib/chat";
import { type ComposerAttachment, composerAttachmentsFromMessage } from "@/lib/chat-attachments";
import type { UploadedFile } from "@/lib/uploaded-file-block";
import { useChatDraftStore } from "@/stores/useChatDraftStore";
import { useChatStore } from "@/stores/useChatStore";
import { useToastStore } from "@/stores/useToastStore";
import { confirmLeaveWithUnsavedFiles } from "@/stores/useUnsavedChangesGuardStore";

/** 分岐先の入力欄へ引き継ぐ元メッセージの内容。 */
export interface BranchDraft {
	text: string;
	images: ImageContentBlock[];
	documents: DocumentContentBlock[];
	uploadedFiles: UploadedFile[];
}

/**
 * 指定ユーザーメッセージの直前までを複製した分岐先チャットを作り、そのメッセージの本文と添付を
 * 分岐先の入力欄の下書きに載せてから遷移する。分岐先の履歴は ChatUrlSync 経由の切り替えで読み込まれる。
 * 分岐先の作成中も編集できるため、未保存のファイルは作成後に確かめる。捨てないと選ばれたら移動しない。
 */
export async function branchFromMessage(
	chatId: string,
	messageId: string,
	draft: BranchDraft,
	navigate: NavigateFunction,
): Promise<void> {
	let branch: ChatMetadata;
	let attachments: ComposerAttachment[];
	try {
		// 添付を先に揃え、画像の取得や変換に失敗したら分岐先を作らない。
		attachments = await composerAttachmentsFromMessage(draft);
		branch = await branchChat(chatId, messageId);
	} catch (err: unknown) {
		useToastStore.getState().show({
			type: "error",
			message: err instanceof Error ? err.message : "チャットの分岐に失敗しました。",
		});
		throw err;
	}
	useChatStore.getState().addChatListItem(branch);
	// 入力欄は chatId ごとに再マウントして下書きから初期化するため、遷移前に載せておけば表示時に入っている。
	const drafts = useChatDraftStore.getState();
	drafts.setDraftText(branch.id, draft.text);
	drafts.setDraftAttachments(branch.id, attachments);
	if (!(await confirmLeaveWithUnsavedFiles())) return;
	navigate(`/chats/${branch.id}`);
}

/** 表示中のチャットを分岐する関数を返す。下書き表示中 (chatId なし) は何もしない。 */
export function useBranchChat(): (messageId: string, draft: BranchDraft) => Promise<void> {
	const navigate = useNavigate();
	const chatId = useChatStore((s) => s.chatId);
	return useCallback(
		async (messageId: string, draft: BranchDraft) => {
			if (chatId === null) return;
			await branchFromMessage(chatId, messageId, draft, navigate);
		},
		[chatId, navigate],
	);
}
